import React, {useEffect, useState} from "react";
import {Projekt} from "../model/projekt.entity";
import {Utils} from "../Utils";
import {dateAsUI, dateFromModel, XDateScale} from "@michalrakus/x-react-web-lib/XUtilsConversions";
import {XUtilsCommon} from "@michalrakus/x-react-web-lib/XUtilsCommon";
import {TypObdobiaEnum, XEnum} from "../common/enums";
import {Pobocka} from "../model/pobocka.entity";
import {MultiSelect, MultiSelectChangeEvent} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";
import {XCalendar} from "@michalrakus/x-react-web-lib/XCalendar";
import {Button} from "primereact/button";

export interface FilterValues {
    selectedPobockaList: Pobocka[];
    typObdobia: XEnum;
    datumOd: Date | null;
    datumDo: Date | null;
    selectedDateList: DateOption[];
}

export interface DateOption {
    date: Date; // mesiac, prip. rok
    dateUI: string; // datum zobrazeny uzivatelovi
}

// sem sme vytiahli filter iba koli tomu ze bol strasne pomaly ak report obsahoval vela riadkov
export const BudgetReportFilter = (props: {
    pobockaSuggestionList: Pobocka[];
    initFilterValues: FilterValues;
    filtrovat: (filterValues: FilterValues) => void;
    zrusitFilter: (filterValues: FilterValues) => void;
}) => {

    const [typObdobia, setTypObdobia] = useState<XEnum>(props.initFilterValues.typObdobia);
    const [datumOd, setDatumOd] = useState<Date | null>(props.initFilterValues.datumOd);
    const [datumDo, setDatumDo] = useState<Date | null>(props.initFilterValues.datumDo);
    const [selectedDateList, setSelectedDateList] = useState<DateOption[]>(props.initFilterValues.selectedDateList); // vyselectovane mesiace/roky
    const [selectedPobockaList, setSelectedPobockaList] = useState<Pobocka[]>(props.initFilterValues.selectedPobockaList); // vyselectovane pobocky

    // nechame "hore"
    //const [pobockaSuggestionList, setPobockaSuggestionList] = useState<Pobocka[]>([]);

    // staci takto do "member premennej", kedze nepotrebujeme zoznam menit, resp. nacitavat z DB
    // UPOZORNENIE: problem "membrov" je ze po zavolani akehokolvek set<XYZ>(<hodnota>)
    // sa spusti cela funkcia BudgetReport (koli render) odhora az nadol a "membre" sa nanovo nainicializuju
    // zatial iba mesacne obdobie, mozno neskor pridame aj dalsie
    const typObdobiaSuggestionList: XEnum[] = [TypObdobiaEnum.Datumy, TypObdobiaEnum.Mesacne/*, TypObdobiaEnum.Stvrtrocne, TypObdobiaEnum.Polrocne, TypObdobiaEnum.Rocne*/];

    const [optionsMesiace, setOptionsMesiace] = useState<DateOption[]>([]);

    useEffect(() => {
        loadOptionsMesiace();
    }, []);

    const loadOptionsMesiace = () => {
        // nacitame mesiace pre aktualny projekt, maximalne po aktualny mesiac (predpokladame ze v buducich mesiacoch este nie su ziadne vydavky)
        const currentProjekt: Projekt | null = Utils.getCurrentProjekt();
        if (!currentProjekt) {
            return []; // nemalo by nastat
        }

        // mesiacOd/mesiacDo dame od zaciatku projektu po aktualny mesiac, maximalne vsak 6 mesiacov po konci projektu (aby aby nam neponukal mesiace napr. 3 roky po skonceni projektu)
        const mesiacOd: Date = dateFromModel(currentProjekt.mesiacOd)!;
        let mesiacDo: Date = XUtilsCommon.currentMonth();
        const projektMesiacDo: Date = dateFromModel(currentProjekt.mesiacDo)!;
        const mesiacDoMax: Date = XUtilsCommon.dateAddMonths(projektMesiacDo, 6)!;
        if (XUtilsCommon.dateCompare(mesiacDo, mesiacDoMax) > 0) { // mesiacDo > mesiacDoMax
            mesiacDo = mesiacDoMax;
        }
        const optionsMesiace: DateOption[] = [];
        for (let mesiac: Date = mesiacDo; XUtilsCommon.dateCompare(mesiac, mesiacOd) >= 0; mesiac = XUtilsCommon.dateAddMonths(mesiac, -1)!) {
            optionsMesiace.push({date: mesiac, dateUI: dateAsUI(mesiac, XDateScale.Month)});
        }

        setOptionsMesiace(optionsMesiace);
    }

    const zrusitFilter = () => {
        // init hodnoty state atributov (hodnoty zodpovedaju state atributom filterValues v BudgetReport)
        const filterValuesInit: FilterValues = {
            selectedPobockaList: [],
            typObdobia: TypObdobiaEnum.Mesacne,
            datumOd: null,
            datumDo: null,
            selectedDateList: []
        }
        setSelectedPobockaList(filterValuesInit.selectedPobockaList);
        setTypObdobia(filterValuesInit.typObdobia);
        setDatumOd(filterValuesInit.datumOd);
        setDatumDo(filterValuesInit.datumDo);
        setSelectedDateList(filterValuesInit.selectedDateList);
        props.zrusitFilter(filterValuesInit);
    }

    // poznamka: v obidvoch MultiSelect-och (pobocky aj mesiace) sme mali maxSelectedLabels={5} ale v novsej verzii Primereact (10.9.5) prestalo fungovat
    // (ak vyselectujem viac ako 5 poloziek, Multiselect nic nezobrazi)
    // tak zobrazujeme vzdy vsetky polozky, pekne sa to zalamuje (wrap funguje)
    // poznamka 2: MultiSelect pre mesiace - umyselne mame dataKey="dateUI" - chceme porovnavat string-y, nie Date-y
    return (
        <div className="flex justify-content-center m-1">
            <label className="m-2">Pobočky</label>
            <MultiSelect options={props.pobockaSuggestionList} optionLabel="nazov" dataKey="id" display="chip" className="mr-2" scrollHeight="30rem"
                         value={selectedPobockaList} onChange={(e: MultiSelectChangeEvent) => setSelectedPobockaList(e.value)}/>
            <Dropdown options={typObdobiaSuggestionList} optionLabel="name"
                      value={typObdobia} onChange={(e: any) => setTypObdobia(e.value)}/>
            {typObdobia.code === TypObdobiaEnum.Datumy.code ?
                [<label className="m-2">Dátum dokladu od</label>,
                    <XCalendar value={datumOd} onChange={(value: Date | null) => setDatumOd(value)}/>,
                    <label className="m-2">do</label>,
                    <XCalendar value={datumDo} onChange={(value: Date | null) => setDatumDo(value)}/>] : null
            }
            {typObdobia.code === TypObdobiaEnum.Mesacne.code ?
                <MultiSelect options={optionsMesiace} optionLabel="dateUI" dataKey="dateUI" display="chip" className="ml-2" scrollHeight="40rem"
                             value={selectedDateList} onChange={(e: MultiSelectChangeEvent) => setSelectedDateList(e.value)}/> : null
            }
            <Button label="Filtrovať (DB)" onClick={() => props.filtrovat({
                selectedPobockaList: selectedPobockaList,
                typObdobia: typObdobia,
                datumOd: datumOd,
                datumDo: datumDo,
                selectedDateList: selectedDateList
            })} className="ml-2"/>
            <Button label="Zrušiť filter" onClick={zrusitFilter} className="ml-2"/>
        </div>
    );
}